import { useEffect, useRef, useState, type FormEvent } from 'react';
import { get, patch, post, ApiError, type FollowUp, type HistoryResponse } from '../lib/api.ts';
import { CHANNELS, PRIORITIES, RESULTS, STATUSES, NEXT_REQUIRED_MSG, noNextNeeded, statusAr, statusTone , syncCompanyCustomer } from '../lib/labels.ts';
import { addDays, cairoToday, cairoWorkDay, displayPhone, shortDate } from '../lib/format.ts';
import { PhoneActions } from '../components/PhoneActions.tsx';
import { DateInput, Alert, Badge, Field, Modal, Select, Spinner, TextArea, TextInput } from '../components/ui.tsx';
import { CustomerTimeline } from './CustomerTimeline.tsx';
import { SourceBadge } from './FollowUpList.tsx';
import { AltPhoneField } from '../components/AltPhoneField.tsx';

/**
 * تفاصيل متابعة + تعديل + السجل التاريخي الكامل لنفس الرقم.
 *
 * **التنسيق (قرار المالك: «كبّر الشاشة وظبّط التنسيق» ثم «خليها من غير Scroll»)**:
 * كانت الحقول أربعةً في الصفّ داخل نافذةٍ ضيّقة، فتُقصَّ الخيارات في منتصف كلماتها
 * («طلب التواصل لاد…») ويخرج المحتوى عن عرض النافذة فيظهر شريط تمرير أفقي. فصارت
 * النافذة `xl` والحقول **اثنين في الصفّ**، ومقسومةً إلى مجموعتين معنونتين: ما جرى في
 * التواصل، وما سيجري بعده. والملاحظات وحدها في صفٍّ كامل — فهي جملةٌ تُقرأ لا خانةٌ تُحشر.
 *
 * ثمّ **يقف السجلّ التاريخي إلى جوار النموذج** على الشاشة الكبيرة لا تحته، فيُقسم
 * الارتفاع نصفين بدل أن يُجمع — وتُقرأ النافذة كلّها بلا تمرير.
 */
export function FollowUpDetails({ followUp, isAdminUser, readOnly = false, onClose, onSaved, onBook, onDeleted, onDone }: {
  followUp: FollowUp; isAdminUser: boolean; onClose: () => void;
  /** متابعة زميل يقرؤها «قارئ متابعات الفريق» (0182) — تُعرض بلا تعديل ولا أزرار، والخادم يرفض الكتابة على أي حال */
  readOnly?: boolean;
  onSaved: (f: FollowUp) => void; onBook?: (f: FollowUp) => void; onDeleted?: (followUpNo: string) => void;
  /** «تحديث موقف المتابعة» (0151) */
  onDone?: (f: FollowUp) => void;
}) {
  const [f, setF] = useState(followUp);
  const [history, setHistory] = useState<HistoryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // الحقائق التاريخية تُصحَّح في يوم التواصل فقط (قاعدة الخادم) — الأدمن بلا قيد. والتاريخ نفسه لا يتغيّر لأحد، والرقم لا
  // يغيّره البياع (0158، 02/10/2026)
  const factsEditable = isAdminUser || f.contactedOn === cairoWorkDay();   // في يوم شغلها (0168)
  const phoneEditable = isAdminUser;

  const [form, setForm] = useState({
    customerName: f.customerName, phone: f.phone, altPhone: f.altPhone ? displayPhone(f.altPhone) : '', contactedOn: f.contactedOn,
    channel: f.channel, result: f.result, customerStatus: f.customerStatus, notes: f.notes ?? '',
    nextFollowUpOn: f.nextFollowUpOn ?? '', priority: f.priority,
  });
  const set = (k: keyof typeof form, v: string) => setForm(s => ({ ...s, [k]: v }));

  useEffect(() => {
    get<HistoryResponse>(`/follow-ups/history?phone=${encodeURIComponent(f.phone)}${readOnly ? '&team=true' : ''}`).then(setHistory).catch(() => setHistory(null));
  }, [f.phone, readOnly]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null); setDone(null);
    if (!form.nextFollowUpOn && !noNextNeeded(form.customerStatus, form.result)) { setError(NEXT_REQUIRED_MSG); return; }
    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        customerName: form.customerName.trim(), customerStatus: form.customerStatus,
        notes: form.notes.trim() || null, priority: form.priority,
        altPhone: form.altPhone.trim() || null,                // الرقم التاني يُعدَّل في أي وقت (0183)
        nextFollowUpOn: form.nextFollowUpOn || null,
        // باليوم فقط (قرار المالك 01/10/2026): ساعةٌ قديمة تُمسح بأوّل تعديل — والتذكير صار أربع مرّات في اليوم (0149)
        nextFollowUpAt: null,
      };
      if (factsEditable) {
        Object.assign(body, { channel: form.channel, result: form.result }, phoneEditable ? { phone: form.phone.trim() } : {});
      }
      const updated = await patch<FollowUp>(`/follow-ups/${f.id}`, body, { 'if-match': String(f.rowVersion) });
      setF(updated); onSaved(updated); setDone('تم حفظ التعديل');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'تعذّر حفظ التعديل');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`المتابعة ${f.firstFollowUpNo ?? f.followUpNo}`} size="xl" onClose={onClose}>
      {/*
       * **بلا تمرير** (قرار المالك): النافذة تُقرأ كلّها بنظرة. وذلك بأن يقف السجلّ
       * التاريخي **إلى جوار** النموذج على الشاشة الكبيرة لا تحته — فيُقسم الارتفاع
       * نصفين بدل أن يُجمع. وتحت `lg` يعودان صفًّا واحدًا لأن العرض لا يتّسع لهما.
       */}
      <div className="space-y-3 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start lg:gap-x-6 lg:space-y-0">
        {/* ترويسة: من ومع من — تُقرأ سطرًا واحدًا قبل النزول إلى الحقول */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm lg:col-span-2">
          <Badge tone={statusTone[f.customerStatus]}>{statusAr(f.customerStatus)}</Badge>
          <span className="font-medium text-ink-900">{f.customerName}</span>
          <SourceBadge f={f} />
          <span dir="ltr" className="tabular-nums text-ink-600"><PhoneActions phone={f.phone} /></span>
          {f.altPhone && <span dir="ltr" className="tabular-nums text-ink-600"><PhoneActions phone={f.altPhone} /></span>}
          <span className="text-ink-500">· {f.salesName}</span>
          {f.convertedToBooking && <Badge tone="bg-emerald-600 text-white">حجز {f.bookingNo}</Badge>}
          {f.closedAt && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-ink-600">موقف قديم — اتحدّث بعده</span>}
          <span className="ms-auto flex flex-wrap gap-2">
            {!readOnly && onDone && !f.closedAt && !f.convertedToBooking && (
              <button type="button" className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-bold text-white hover:bg-emerald-700"
                onClick={() => onDone(f)}>🔄 تحديث موقف المتابعة</button>
            )}
            {!readOnly && onBook && !f.convertedToBooking && (
              <button type="button" className="btn-primary px-3 py-1.5 text-sm" onClick={() => onBook(f)}>إنشاء حجز</button>
            )}
          </span>
        </div>

        <form onSubmit={onSubmit} aria-label="تعديل المتابعة" className="space-y-4 lg:mt-3">
          {readOnly && <Alert kind="info">متابعة {f.salesName} — بتشوفها للتوجيه بس، والتعديل لصاحبها.</Alert>}
          <fieldset disabled={readOnly} className="m-0 min-w-0 space-y-4 border-0 p-0">
          {error && <Alert>{error}</Alert>}
          {done && <Alert kind="success">{done}</Alert>}
          {!factsEditable && (
            <Alert kind="info">
              تفاصيل التواصل (الرقم والتاريخ والنوع والنتيجة) مقفلة بعد يوم التواصل — سجّل متابعة جديدة بدلًا من تعديلها.
            </Alert>
          )}

          {/* ما جرى */}
          <fieldset className="space-y-2.5">
            <legend className="mb-1 text-sm font-semibold text-ink-800">تفاصيل التواصل</legend>
            <div className="grid gap-x-4 gap-y-2.5 sm:grid-cols-2">
              <Field label="اسم العميل" required>
                <TextInput value={form.customerName} required maxLength={150} onChange={e => set('customerName', e.target.value)} />
              </Field>
              <Field label="رقم الهاتف">
                <TextInput dir="ltr" value={form.phone} disabled={!phoneEditable} onChange={e => set('phone', e.target.value)} />
              </Field>
              <AltPhoneField value={form.altPhone} onChange={v => set('altPhone', v)} />
              <Field label="نوع التواصل">
                <Select options={CHANNELS} value={form.channel} disabled={!factsEditable} onChange={e => set('channel', e.target.value)} />
              </Field>
              <Field label="تاريخ التواصل">
                <TextInput value={shortDate(form.contactedOn)} disabled readOnly className="tabular-nums" />
              </Field>
              <Field label="نتيجة التواصل">
                <Select options={RESULTS} value={form.result} disabled={!factsEditable} onChange={e => { const v = e.target.value; setForm(cur => syncCompanyCustomer(cur, 'result', v)); }} />
              </Field>
            </div>
          </fieldset>

          {/* وما سيجري بعده */}
          <fieldset className="space-y-2.5 border-t border-slate-200 pt-3">
            <legend className="mb-1 text-sm font-semibold text-ink-800">المتابعة القادمة</legend>
            <div className="grid gap-x-4 gap-y-2.5 sm:grid-cols-2">
              <Field label="موقف العميل" required>
                <Select options={STATUSES} value={form.customerStatus} onChange={e => { const v = e.target.value; setForm(cur => syncCompanyCustomer(cur, 'customerStatus', v)); }} />
              </Field>
              <Field label="الأولوية">
                <Select options={PRIORITIES} value={form.priority} onChange={e => set('priority', e.target.value)} />
              </Field>
              <Field label="يوم المتابعة القادمة" required={!noNextNeeded(form.customerStatus, form.result)}
                hint={noNextNeeded(form.customerStatus, form.result) ? 'مش لازم — العميل غير مهتم أو فقدناه' : 'لازم — والبرنامج بيفكّر بيها في اليوم ده 4 مرّات'}>
                <DateInput value={form.nextFollowUpOn} min={addDays(cairoToday(), 1)} onChange={v => set('nextFollowUpOn', v)} />
              </Field>
            </div>
            {/* الملاحظات جملةٌ تُقرأ لا خانةٌ تُحشر: صفٌّ كامل وأربعة أسطر */}
            <Field label="ملاحظات">
              <TextArea rows={3} maxLength={2000} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </Field>
          </fieldset>

          </fieldset>
          {!readOnly && <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3">
            <button type="submit" className="btn-primary px-6" disabled={busy}>{busy ? 'جارٍ الحفظ…' : 'حفظ التعديل'}</button>
            {/* الحذف للأدمن وحده (قرار المالك) — والخادم والقاعدة يفرضانه، والإخفاء ليس الحماية */}
            {isAdminUser && onDeleted && (
              <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}
                className="ms-auto rounded-lg border border-rose-300 px-4 py-2 text-sm font-medium text-rose-700 transition-colors hover:bg-rose-50">
                حذف المتابعة
              </button>
            )}
          </div>}
        </form>

        <section aria-label="سجل التواصل" className="border-t border-slate-200 pt-3 lg:mt-3 lg:rounded-lg lg:border lg:bg-slate-50/50 lg:p-3">
          <h3 className="mb-2 text-sm font-semibold text-ink-900">سجل التواصل الكامل لهذا الرقم</h3>
          {history === null ? <Spinner /> : (
            <>
              {history.customer.exists && (
                <p className="mb-2 text-sm text-ink-500">
                  عميل مسجّل: {history.customer.previousName} · حجوزات سابقة: {history.customer.bookingsCount} · آخر حجز: {shortDate(history.customer.lastBookingDate)}
                </p>
              )}
              {/* سجلّ العميل (0151): كل تواصل بملاحظاته — الأحدث فوق */}
              <div className="max-h-72 overflow-y-auto pe-1 lg:max-h-[24rem]">
                <CustomerTimeline items={history.items} currentId={f.id} showSeller={isAdminUser || readOnly} />
              </div>
            </>
          )}
        </section>
      </div>

      {confirmDelete && onDeleted && (
        <DeleteFollowUpDialog
          followUp={f}
          onClose={() => setConfirmDelete(false)}
          onDeleted={no => { setConfirmDelete(false); onDeleted(no); }}
        />
      )}
    </Modal>
  );
}

/**
 * تأكيد حذف المتابعة — بنفس حارسَي حذف الحجز: قراءةُ ما سيُحذف، ثم كتابة رقمه.
 *
 * **وما يحدث فعلًا أرشفة**: الصفّ يبقى ومعه من حذفه ومتى، ويخرج من كل قائمة وملخّص
 * وتقييم. والنافذة تقول ذلك بدل أن توهم بمحوٍ لم يقع — ولو أوهمت لظنّ الأدمن أنّ
 * أثرها زال من سجلّ التدقيق وهو باقٍ.
 */
function DeleteFollowUpDialog({ followUp, onClose, onDeleted }: {
  followUp: FollowUp; onClose: () => void; onDeleted: (followUpNo: string) => void;
}) {
  // كان يُطلب كتابةُ رقم المتابعة، فأُلغي (قرار المالك 23/09/2026: «خلّيها بتأكيد فقط») — كما في
  // حذف الحجز. والخادم ما زال يطلب الرقم ويطابقه قبل الكتابة، ترسله الواجهة عن المعروض.
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // قفلٌ متزامن: `busy` حالةٌ تُحدَّث لاحقًا، فنقرتان سريعتان كانتا تُرسلان طلبين
  const inFlight = useRef(false);

  async function confirm() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setError(null);
    try {
      await post(`/admin/follow-ups/${followUp.id}/archive`, { followUpNo: followUp.followUpNo });
      onDeleted(followUp.followUpNo);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر حذف المتابعة — لم يتغيّر شيء');
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <Modal title="حذف المتابعة؟" size="sm" onClose={() => { if (!busy) onClose(); }}>
      <div className="space-y-3">
        <dl className="grid gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
          <div><dt className="inline text-ink-500">رقم المتابعة: </dt>
            <dd className="inline font-medium tabular-nums text-ink-900" dir="ltr">{followUp.followUpNo}</dd></div>
          <div><dt className="inline text-ink-500">العميل: </dt>
            <dd className="inline text-ink-900">{followUp.customerName}</dd></div>
          <div><dt className="inline text-ink-500">البياع: </dt>
            <dd className="inline text-ink-900">{followUp.salesName}</dd></div>
        </dl>

        <Alert>حذف المتابعة نهائي ولا يمكن التراجع عنه.</Alert>
        <p className="text-xs text-ink-500">
          تخرج المتابعة من القائمة ومن ملخّصات النشاط والتقييم. ويبقى سجلّ التدقيق كما هو —
          فيُعرف من حذفها ومتى.
        </p>

        {error && <Alert>{error}</Alert>}

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-ghost px-4 py-2" disabled={busy} autoFocus onClick={onClose}>إلغاء</button>
          <button type="button" disabled={busy} onClick={() => void confirm()}
            className="rounded-lg border border-rose-300 bg-rose-600 px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-rose-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-200 disabled:text-ink-500 disabled:shadow-none">
            {busy ? 'جارٍ الحذف…' : 'تأكيد حذف المتابعة'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
